import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { X, Activity, TrendingUp, TrendingDown } from 'lucide-react';

const AdminUserTrades = ({ user, onClose }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setPositions(await adminAPI.userPositions(user.id)); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  const editField = async (p, field) => {
    const val = window.prompt(`New ${field} for ${p.display || p.symbol}`, p[field]);
    if (val === null) return;
    const num = parseFloat(val);
    if (Number.isNaN(num)) { toast({ title: 'Invalid number' }); return; }
    try { await adminAPI.editPosition(p.id, { [field]: num }); toast({ title: 'Position updated' }); load(); }
    catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const closeAt = async (p) => {
    const val = window.prompt(`Close price for ${p.display || p.symbol}`, p.open_price);
    if (val === null) return;
    const num = parseFloat(val);
    if (Number.isNaN(num)) { toast({ title: 'Invalid price' }); return; }
    try { const r = await adminAPI.closePosition(p.id, num); toast({ title: 'Position closed', description: `P&L ${r.trade.pnl >= 0 ? '+' : ''}$${r.trade.pnl.toFixed(2)}` }); load(); }
    catch (e) { toast({ title: 'Close failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const settle = async (p, sign) => {
    const def = sign === 'win' ? '100' : '-100';
    const val = window.prompt(`Force ${sign === 'win' ? 'PROFIT' : 'LOSS'} amount ($) for ${p.display || p.symbol}`, def);
    if (val === null) return;
    let num = parseFloat(val);
    if (Number.isNaN(num)) { toast({ title: 'Invalid amount' }); return; }
    num = sign === 'win' ? Math.abs(num) : -Math.abs(num);
    try { const r = await adminAPI.settlePosition(p.id, num); toast({ title: 'Trade settled', description: `Booked ${num >= 0 ? '+' : ''}$${num.toFixed(2)}` }); load(); }
    catch (e) { toast({ title: 'Settle failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const settleAll = async (mode) => {
    const val = window.prompt(`Force ALL ${positions.length} open trade(s) to ${mode === 'win' ? 'PROFIT' : 'LOSS'}.\nAmount ($) applied to EACH trade:`, '100');
    if (val === null) return;
    const num = parseFloat(val);
    if (Number.isNaN(num) || num <= 0) { toast({ title: 'Invalid amount' }); return; }
    try {
      const r = await adminAPI.settleAll(user.id, mode, num);
      toast({ title: `Rigged ${r.settled} trade(s)`, description: `Each booked ${mode === 'win' ? '+' : '-'}$${num.toFixed(2)}` });
      load();
    } catch (e) { toast({ title: 'Rig failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl p-6 rounded-xl border border-[#1a2823] bg-[#0a1210] max-h-[85vh] overflow-y-auto" data-testid="admin-user-trades">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-bold flex items-center gap-2"><Activity size={16} className="text-purple-400"/> Live Trades</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={16}/></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">{user.name} · {user.email}</p>
        {!loading && positions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => settleAll('win')} data-testid="rig-all-win-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#00e676]/40 bg-[#00e676]/10 text-[#00e676] text-xs font-semibold hover:bg-[#00e676]/20">
              <TrendingUp size={13}/> Force ALL to Profit
            </button>
            <button onClick={() => settleAll('loss')} data-testid="rig-all-loss-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20">
              <TrendingDown size={13}/> Force ALL to Loss
            </button>
          </div>
        )}
        {loading && <div className="py-8 text-center text-sm text-gray-500">Loading…</div>}
        {!loading && positions.length === 0 && <div className="py-10 text-center text-sm text-gray-500">No open positions</div>}
        {!loading && positions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] text-gray-500 uppercase">
                <th className="py-2">Symbol</th><th>Side</th><th>Volume</th><th>Entry</th><th>SL</th><th>TP</th><th className="text-right pr-2">Actions</th>
              </tr></thead>
              <tbody>
                {positions.map(p => (
                  <tr key={p.id} className="border-t border-[#141f1b]" data-testid={`admin-pos-${p.id}`}>
                    <td className="py-2 text-white font-semibold">{p.display || p.symbol}</td>
                    <td className={p.side === 'Buy' ? 'text-[#00e676]' : 'text-red-400'}>{p.side}</td>
                    <td className="mono text-gray-300 cursor-pointer hover:text-white" onClick={() => editField(p, 'volume')} title="Click to edit">{p.volume}</td>
                    <td className="mono text-gray-300 cursor-pointer hover:text-white" onClick={() => editField(p, 'open_price')} title="Click to edit">{p.open_price}</td>
                    <td className="mono text-gray-400 cursor-pointer hover:text-white" onClick={() => editField(p, 'sl')} title="Click to edit">{p.sl || '—'}</td>
                    <td className="mono text-gray-400 cursor-pointer hover:text-white" onClick={() => editField(p, 'tp')} title="Click to edit">{p.tp || '—'}</td>
                    <td className="text-right pr-2 whitespace-nowrap">
                      <button onClick={() => settle(p, 'win')} data-testid={`force-win-${p.id}`} title="Force profit" className="p-1 mr-1 rounded border border-[#1a2823] text-[#00e676] hover:border-[#00e676]/50"><TrendingUp size={12}/></button>
                      <button onClick={() => settle(p, 'loss')} data-testid={`force-loss-${p.id}`} title="Force loss" className="p-1 mr-1 rounded border border-[#1a2823] text-red-400 hover:border-red-500/50"><TrendingDown size={12}/></button>
                      <button onClick={() => closeAt(p)} data-testid={`admin-close-${p.id}`} className="px-2 py-1 rounded border border-[#1a2823] text-gray-300 hover:text-white">Close</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserTrades;
